<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuth } from '/app/composables/auth';
import { date } from '@bignerve/ui-utils';
import Markdown from '/app/components/NMarkdown.vue';
import { track } from '/app/utils/analytics';
import { renderNeuron } from './template-context';

const props = defineProps({
    detail: {
        type: Object,
    },
    simple: {
        type: Boolean,
        default: false,
    },
    // Origin surface this notification is rendered in (e.g. 'gamedash'). When
    // set, clicks on the challenge link fire an explicit Heap event tagged with
    // this source. The tag lives in the event payload, not the URL, so the
    // address bar stays clean. Left null elsewhere (e.g. the notifications
    // page), where the link keeps its native behaviour.
    source: {
        type: String,
        default: null,
    },
});

const { auth: $auth } = useAuth();
const router = useRouter();
const { t } = useI18n();

// Challenge entity as hydrated from template_vars (hydrateNeuron), used by the
// analytics payload.
const challenge = computed(() => props.detail.contexts?.challenge || {});

const computedClass = computed(() => [
    `is-${props.detail.type}`,
    `from-${props.detail.user_origin}`,
    `is-${props.detail.role}`
]);

// Admin template rendered to markdown — string templates are pre-rendered by
// the backend, object ones go through the Handlebars pass. useNeurons renders
// it ahead to collapse repeats; items reaching here another way (realtime
// LatestNotifications) are rendered on the spot. Null when the definition has
// no template: the item is then not rendered at all.
const neuron = computed(() => ({
    ...props.detail,
    point_created_at: date(props.detail.point_created_at, 'human'),
    context: props.detail.statement
        ?? renderNeuron(props.detail, { router, user: $auth.user }),
}));

const meta = computed(() => [
    neuron.value.point_created_at,
    props.detail.repeats > 1 && t('component.game-dash.repeats', props.detail.repeats),
].filter(Boolean).join(' · '));

function onNotificationClick(navigate) {
    track('Game Dash Notification Clicked', {
        notification_type: 'neuron-earned',
        role: props.detail.role,
        points: props.detail.points,
        challenge_title: challenge.value.title,
        challenge_slug: challenge.value.slug,
        meaningful_name: props.detail.meaningful_name,
    });

    navigate();
}

// Fired by NMarkdown when the challenge link inside the notification text is
// clicked. This is the link that actually reaches the challenge details view.
function onChallengeLinkClick({ href, event }) {
    track('Game Dash Notification Clicked', {
        notification_type: 'neuron-earned',
        destination: 'challenge',
        role: props.detail.role,
        points: props.detail.points,
        challenge_title: challenge.value.title,
        challenge_slug: challenge.value.slug,
        meaningful_name: props.detail.meaningful_name,
        source: props.source,
    });

    // Markdown renders raw <a> tags with no SPA interception, so an internal
    // link would full-reload and could drop the track() call above. Take over
    // plain left-clicks and route via vue-router instead; modified clicks
    // (open-in-new-tab, etc.) fall through untouched.
    const isInternal = href?.startsWith('/');
    const isPlainClick = event.button === 0
        && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

    if (isInternal && isPlainClick) {
        event.preventDefault();
        router.push(href);
    }
}
</script>


<template>
    <div v-if="neuron.context" class="game-dash-list-item" data-heap-id="gamedash-notification" data-heap-notification-type="neuron-earned" :class="computedClass">
        <RouterLink v-if="$auth.authenticated" :to="{ name: 'dashboard.notifications', params: { platformSlug: $auth.user.screen_name || 'me' }}" v-slot="{ navigate }" custom data-heap-context="gamedash" data-heap-target="neuron-earned">
            <span :title="neuron.points" class="game-dash-list-item__point cursor-pointer" @click="onNotificationClick(navigate)">
                <span v-if="neuron.points > 0">+</span>
                {{ Math.ceil(neuron.points).toFixed(0) }}
            </span>
        </RouterLink>
        <div class="game-dash-list-item__content">
            <Markdown class="game-dash-list-item__text opacity-95 leading-5" :code="neuron.context" inline @link-clicked="onChallengeLinkClick" />
            <div v-if="!simple" class="game-dash-list-item__meta">{{ meta }}</div>
        </div>
    </div>
</template>
